export interface SeatDomBadgeProps {
  displayName: string;
  handCount: number;
  title: string;
  isCurrentTurn: boolean;
  hasPassed: boolean;
  isBot: boolean;
  turnOrder: number;
  lastRoundPlacement: number;
}

// Minimal "poker-style" seat marker: a small fan of face-down card backs + a
// name pill with the turn order. We intentionally do NOT show the exact
// remaining card count during play (strategic info) — the backs just signal
// that the seat still holds cards.
export class SeatDomBadge {
  el: HTMLDivElement;
  private cardsEl: HTMLDivElement;
  private nameEl: HTMLDivElement;
  private scale = 1;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.style.cssText = `
      position: absolute;
      transform: translate(-50%, -50%);
      pointer-events: none;
      text-align: center;
      z-index: 30;
      transition: opacity 0.2s, transform 0.2s, filter 0.2s;
    `;
    this.cardsEl = document.createElement('div');
    this.cardsEl.style.cssText =
      'display:flex; justify-content:center; align-items:flex-end; height:74px; margin-bottom:7px;';
    this.nameEl = document.createElement('div');
    this.nameEl.style.cssText = `
      display:inline-flex; align-items:center; gap:7px;
      padding:5px 16px; border-radius:16px;
      background:rgba(10,5,7,0.55);
      border:1px solid rgba(245,200,105,0.35);
      color:#f4ecd6; font-weight:700; font-size:24px; white-space:nowrap;
      font-family:"Apple SD Gothic Neo","Segoe UI",sans-serif;
      text-shadow:0 1px 2px rgba(0,0,0,0.7);
      box-shadow:0 3px 10px rgba(0,0,0,0.4);
    `;
    this.el.appendChild(this.cardsEl);
    this.el.appendChild(this.nameEl);
    parent.appendChild(this.el);
  }

  setPosition(x: number, y: number): void {
    this.el.style.left = `${x}px`;
    this.el.style.top = `${y}px`;
  }

  // Responsive scale (1 desktop, smaller on phones).
  setScale(s: number): void {
    this.scale = s;
  }

  private applyTransform(turn: boolean): void {
    const z = turn ? this.scale * 1.08 : this.scale;
    this.el.style.transform = `translate(-50%, -50%) scale(${z})`;
  }

  update(props: SeatDomBadgeProps): void {
    const out = props.handCount <= 0;

    // Face-down fan — a fixed small fan, NOT the real count.
    this.cardsEl.innerHTML = '';
    if (!out) {
      const fan = 3;
      for (let i = 0; i < fan; i++) {
        const back = document.createElement('div');
        const rot = (i - (fan - 1) / 2) * 9;
        back.style.cssText = `
          width: 48px; height: 72px;
          background-image: url('/cards/back.png');
          background-size: cover; background-position: center;
          border: 2px solid #c89b2f; border-radius: 6px;
          margin-left: ${i === 0 ? '0' : '-30px'};
          transform: rotate(${rot}deg);
          box-shadow: 1px 2px 5px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.18);
        `;
        this.cardsEl.appendChild(back);
      }
    }

    // Name pill: "#order  name", plus a state suffix.
    const order =
      props.turnOrder > 0
        ? `<span style="color:#f5c869; font-size:18px;">#${props.turnOrder}</span>`
        : '';
    const name = this.escape(this.truncate(props.displayName, 12));
    let suffix = '';
    if (props.hasPassed) {
      suffix = ` <span style="color:#9aa1ad; font-size:18px;">· PASS</span>`;
    } else if (out) {
      suffix = ` <span style="color:#9aa1ad; font-size:18px;">· 완료</span>`;
    }
    this.nameEl.innerHTML = `${order}<span>${name}</span>${suffix}`;

    if (props.isCurrentTurn) {
      this.nameEl.style.background = 'linear-gradient(180deg,#ffe49a,#f5c869)';
      this.nameEl.style.color = '#3a1d08';
      this.nameEl.style.borderColor = '#efc659';
      this.el.style.filter = 'drop-shadow(0 0 10px rgba(245,200,105,0.7))';
      this.el.style.opacity = '1';
      this.el.style.animation = 'turnPulse 1.6s ease-in-out infinite';
      this.applyTransform(true);
    } else {
      this.nameEl.style.background = 'rgba(10,5,7,0.55)';
      this.nameEl.style.color = '#f4ecd6';
      this.nameEl.style.borderColor = 'rgba(245,200,105,0.35)';
      this.el.style.filter = 'none';
      this.el.style.animation = 'none';
      this.el.style.opacity = props.hasPassed || out ? '0.5' : '0.95';
      this.applyTransform(false);
    }
  }

  destroy(): void {
    this.el.remove();
  }

  private truncate(s: string, max: number): string {
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  }

  private escape(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
